import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Link from "next/link";
import debounce from "debounce";
import Highlighter from "react-highlight-words";

import { useCommunity } from "domain/community/communityContext";
import Avatar from "lib/components/Avatar";
import IconComment from "lib/assets/comment.svg";
import IconHeart from "lib/assets/heart.svg";
import IconHeartFilled from "lib/assets/heart-filled.svg";
import IconNews from "lib/assets/news.svg";
import { useTheme } from "lib/contexts/themeContext";
import { fromNowRecent } from "lib/date";
import { useAuth } from "domain/auth/authContext";
import { useRouter } from "next/router";
import useIsResMobile from "lib/hooks/useIsResMobile";
import Timeline from "lib/components/Timeline";
import IconCertified from "lib/assets/badge-certified.svg";

interface PostProps {
  post: ResCommunityPostNews | ResCommunityPostPost;
  menu?: React.ReactNode;
  isDetail?: boolean;
  onUpdate?: (post: ResCommunityPostPost | ResCommunityPostNews) => void;
  className?: string;
  hideCategory?: boolean;
  highlightWords?: string[];
}

/**
 * @example
 *   <Post post={dataPost} onUpdate={(post) => { ... }} />
 */
const Post = React.memo<PostProps>(
  ({
    post,
    menu,
    isDetail,
    onUpdate,
    className,
    hideCategory,
    highlightWords,
  }) => {
    if (post.postType === "news") {
      return (
        <PostTypeNews
          className={className}
          post={post}
          isDetail={isDetail}
          onUpdate={onUpdate}
        />
      );
    }

    if (post.postType === "post") {
      return (
        <PostTypePost
          className={className}
          post={post}
          isDetail={isDetail}
          menu={menu}
          onUpdate={onUpdate}
          hideCategory={hideCategory}
          highlightWords={highlightWords}
        />
      );
    }

    return null;
  },
);

export default Post;

interface PostTypePostProps {
  post: ResCommunityPostPost;
  menu?: React.ReactNode;
  isDetail?: boolean;
  onUpdate?: (post: ResCommunityPostPost) => void;
  className?: string;
  hideCategory?: boolean;
  highlightWords?: string[];
}

/**
 * Displays community post of `post` type
 */
const PostTypePost: React.FC<PostTypePostProps> = ({
  post,
  isDetail,
  menu,
  onUpdate,
  className,
  hideCategory,
  highlightWords,
}) => {
  return (
    <div className={className}>
      {/* Header */}
      <PostHeader
        type="post"
        postSlug={post.urlSlug}
        name={post.post.name}
        categoryId={post.categoryId}
        profileImage={post.post.profileImage}
        createdAt={post.createdAt}
        isDetail={isDetail}
        hideCategory={hideCategory}
        menu={menu}
        isUserCertified={
          post.writerType === "lawfully" || post.writerType === "lawyer"
        }
      />
      {/* Post Title */}
      <LinkComp disabled={isDetail} postSlug={post.urlSlug}>
        <PostTitle large={isDetail} highlightWords={highlightWords}>
          {post.post.title}
        </PostTitle>
      </LinkComp>
      <LinkComp disabled={isDetail} postSlug={post.urlSlug}>
        {/* Post Contents */}
        <PostContents
          isDetail={isDetail}
          contents={post.post.contents}
          highlightWords={highlightWords}
        />
        {/* Post Timeline */}
        {post.isWithTimeline && (post.timelineSummary || post.timeline) && (
          <PostTimeline timeline={post.timelineSummary || post.timeline} />
        )}
      </LinkComp>
      {/* Footer */}
      <PostFooter
        isLiked={post.isLiked}
        likeCount={post.likeCount}
        commentCount={post.commentCount}
        postId={post.postId}
        postSlug={post.urlSlug}
        onUpdate={onUpdate}
      />
    </div>
  );
};

interface PostTypeNewsProps {
  post: ResCommunityPostNews;
  isDetail?: boolean;
  onUpdate?: (post: ResCommunityPostNews) => void;
  className?: string;
}

/**
 * Displays community post of `news` type
 */
const PostTypeNews: React.FC<PostTypeNewsProps> = ({
  post,
  isDetail,
  onUpdate,
  className,
}) => {
  return (
    <div className={className}>
      {/* Header */}
      <PostHeader
        type="news"
        isUserCertified
        postSlug={post.urlSlug}
        name={post.news.newsName}
        profileImage={post.news.newsProfileImage}
        createdAt={post.createdAt}
        isDetail={isDetail}
      />
      {/* News Image */}
      {post.news.newsImage &&
        (isDetail ? (
          <a href={post.news.newsUrl} target="_blank" rel="noreferrer noopener">
            <NewsImage src={post.news.newsImage} />
          </a>
        ) : (
          <Link href={`/community/posts/${post.urlSlug}`} passHref>
            <a>
              <NewsImage src={post.news.newsImage} />
            </a>
          </Link>
        ))}
      {/* Lawfully's Pick */}
      {post.news.isOurPick && <LawfullysPick />}
      {/* News Title */}
      {isDetail ? (
        <a
          css={`
            color: black;
            :hover {
              color: black;
            }
          `}
          href={post.news.newsUrl}
          target="_blank"
          rel="noreferrer noopener"
        >
          <PostTitle
            css={`
              margin-bottom: 4px;
            `}
            large={isDetail}
          >
            {post.news.newsTitle}
          </PostTitle>
        </a>
      ) : (
        <Link href={`/community/posts/${post.urlSlug}`} passHref>
          <a>
            <PostTitle
              css={`
                margin-bottom: 4px;
              `}
              large={isDetail}
            >
              {post.news.newsTitle}
            </PostTitle>
          </a>
        </Link>
      )}
      {/* News Source */}
      <NewsSource name={post.news.newsSource} href={post.news.newsUrl} />
      {/* Footer */}
      <PostFooter
        isLiked={post.isLiked}
        likeCount={post.likeCount}
        commentCount={post.commentCount}
        postId={post.postId}
        postSlug={post.urlSlug}
        onUpdate={onUpdate}
      />
    </div>
  );
};

const NewsImage = ({ src }: { src: string }) => {
  return (
    <div
      css={`
        margin-bottom: 20px;
        position: relative;
        width: 100%;
        height: 0;
        padding-bottom: 50%;
        border-radius: 10px;
        background-color: #f1f3f5;
        background-image: url(${src});
        background-size: cover;
        background-position: center;
      `}
    />
  );
};
const LawfullysPick = () => {
  return (
    <div
      css={`
        margin-bottom: 10px;
        border: 1px solid #195dee;
        border-radius: 5px;
        padding: 5px 6px;
        line-height: 10px;
        font-size: 10px;
        color: #195dee;
        display: inline-block;
      `}
    >
      Lawfully&apos;s Pick
    </div>
  );
};

const NewsSource = (props: { href: string; name: string }) => {
  return (
    <div
      css={`
        margin-bottom: 10px;
      `}
    >
      <a
        css={`
          line-height: 18px;
          font-size: 12px;
          color: #777;
          :hover {
            color: #777;
          }
        `}
        href={props.href}
        target="_blank"
        rel="noreferrer noopener"
      >
        {props.name}
      </a>
    </div>
  );
};

interface PostHeaderProps {
  type: "post" | "news";
  postSlug: string;
  name: string;
  profileImage: string;
  createdAt: string;
  categoryId?: number;
  isDetail?: boolean;
  menu?: React.ReactNode;
  hideCategory?: boolean;
  isUserCertified?: boolean;
}

const PostHeader: React.FC<PostHeaderProps> = ({
  type,
  postSlug,
  name,
  profileImage,
  createdAt,
  categoryId,
  isDetail,
  menu,
  isUserCertified,
  hideCategory,
}) => {
  const { palette } = useTheme();
  const {
    state: { dataListCommunityCategory },
  } = useCommunity();
  const isLawfullyNews = type === "news" && !profileImage && !name;
  const category = useMemo(() => {
    return dataListCommunityCategory.find(
      (x: ResCategoryCommunity) => x.categoryId === categoryId,
    );
  }, [dataListCommunityCategory, categoryId]);

  return (
    <div
      css={`
        margin-bottom: 10px;
        padding: 20px 0;
      `}
    >
      <div
        css={`
          display: flex;
        `}
      >
        <div
          css={`
            margin-right: 10px;
            width: 44px;
          `}
        >
          <LinkComp disabled={isDetail} postSlug={postSlug}>
            {isLawfullyNews ? (
              <IconNews
                css={`
                  border-radius: 50%;
                `}
              />
            ) : (
              <Avatar src={profileImage} letter={name?.[0]} />
            )}
          </LinkComp>
        </div>
        <div
          css={`
            overflow: hidden;
            margin-right: 10px;
            flex: 1;
            display: flex;
            flex-direction: column;
          `}
        >
          <LinkComp disabled={isDetail} postSlug={postSlug}>
            <div
              css={`
                display: flex;
                align-items: center;
              `}
            >
              <div
                css={`
                  line-height: 22px;
                  font-size: 16px;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                `}
              >
                {isLawfullyNews ? "Lawfully News" : name}
              </div>
              {isUserCertified && (
                <IconCertified
                  css={`
                    margin-left: 4px;
                  `}
                />
              )}
            </div>
            <div
              css={`
                line-height: 20px;
                font-size: 14px;
                color: ${palette.grey[500]};
              `}
            >
              {fromNowRecent(createdAt)}
            </div>
          </LinkComp>
        </div>

        <CategoryOrMenu
          menu={menu}
          category={!hideCategory ? category : undefined}
        />
      </div>
    </div>
  );
};

interface CategoryOrMenuProps {
  menu: React.ReactNode;
  category?: ResCategoryCommunity;
}

const CategoryOrMenu: React.FC<CategoryOrMenuProps> = ({ menu, category }) => {
  const { breakpoints } = useTheme();

  return (
    <>
      {/* Small screen */}
      <div
        css={`
          display: none;
          ${breakpoints.down("sm")} {
            display: block;
          }
        `}
      >
        {category && <PostCategory category={category} />}
      </div>

      {/* Large screen */}
      <div
        css={`
          ${breakpoints.down("sm")} {
            display: none;
          }
        `}
      >
        {menu ? menu : category && <PostCategory category={category} />}
      </div>
    </>
  );
};

interface PostCategoryProps {
  category?: ResCategoryCommunity;
}

const PostCategory: React.FC<PostCategoryProps> = ({ category }) => {
  const { palette } = useTheme();

  return (
    <Link href={`/community/categories/${category?.urlSlug}`} passHref>
      <a
        css={`
          line-height: 22px;
          font-size: 14px;
          color: ${palette.grey[500]};
          :hover {
            text-decoration: underline;
            color: ${palette.grey[500]};
          }
        `}
      >
        {category?.name}
      </a>
    </Link>
  );
};

const PostTitle: React.FC<{
  large?: boolean;
  className?: string;
  highlightWords?: string[];
}> = ({ large, className, children, highlightWords }) => {
  const theme = useTheme();

  return (
    <div
      css={`
        margin-bottom: ${large ? "20px" : "10px"};
        line-height: ${large ? "34px" : "24px"};
        font-size: ${large ? "24px" : "18px"};
        font-weight: 700;
        color: black;
        overflow-wrap: break-word;
        word-wrap: break-word;
        word-break: break-word;
        hyphens: auto;
      `}
      className={className}
    >
      <Highlighter
        textToHighlight={children as string}
        searchWords={highlightWords ?? []}
        highlightStyle={{
          backgroundColor: theme.palette.blueGrey[300],
        }}
      />
    </div>
  );
};

interface PostContentsProps {
  contents: string;
  isDetail?: boolean;
  highlightWords?: string[];
}

const PostContents: React.FC<PostContentsProps> = ({
  contents,
  isDetail,
  highlightWords,
}) => {
  const LINE_HEIGHT = 24;
  const isResMobile = useIsResMobile();
  const maxLines = isResMobile ? 10 : 20;
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(isDetail || !maxLines);
  const [isExpandVisible, setIsExpandVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const contentChunks = useMemo(
    () => contents.split(/(^|\B)(#(?![0-9-_]+\b)[a-zA-Z0-9-_]{1,50})(\b|\r)/g),
    [contents],
  );

  const handleOnExpand = useCallback((e) => {
    e.preventDefault();
    setIsExpanded(true);
  }, []);

  useEffect(() => {
    if (ref.current.clientHeight >= maxLines * LINE_HEIGHT) {
      setIsExpandVisible(true);
    }
  }, [maxLines, isExpandVisible]);

  return (
    <div
      css={`
        margin-bottom: 10px;
        line-height: ${LINE_HEIGHT}px;
        font-size: 16px;
        color: #0b0b0b;
      `}
    >
      <div
        css={`
          white-space: pre-line;
          overflow: hidden;
          max-height: ${isExpanded ? "auto" : `${maxLines * LINE_HEIGHT}px`};
          overflow-wrap: break-word;
          word-wrap: break-word;
          word-break: break-word;
          hyphens: auto;
        `}
        ref={ref}
      >
        {contentChunks.map((chunk, i) => {
          if (chunk.startsWith("#")) {
            return <HashTag key={i} tag={chunk} />;
          }
          return (
            <Highlighter
              key={i}
              textToHighlight={chunk}
              searchWords={highlightWords ?? []}
              highlightStyle={{
                backgroundColor: theme.palette.blueGrey[300],
              }}
            />
          );
        })}
      </div>

      {isExpandVisible && !isExpanded && (
        <button
          css={`
            margin-top: 10px;
            border: none;
            background: none;
            font-size: 14px;
            line-height: 14px;
            color: ${theme.palette.primary.main};
            cursor: pointer;
          `}
          onClick={handleOnExpand}
        >
          ...See More
        </button>
      )}
    </div>
  );
};

const HashTag = ({ tag }: { tag: string }) => {
  return (
    <Link
      href={`/community/tags/[tagId]`}
      as={`/community/tags/${tag.split(/#/)[1]}`}
    >
      <span
        css={`
          color: ${({ theme }) => theme.palette.primary.main};
          cursor: pointer;
          :hover {
            color: ${({ theme }) => theme.palette.primary.dark};
          }
        `}
      >
        {tag}
      </span>
    </Link>
  );
};

interface PostTimelineProps {
  timeline: ResCaseTimeline | ResCaseTimelineSummary;
}

const PostTimeline: React.FC<PostTimelineProps> = ({ timeline }) => (
  <div
    css={`
      margin: 30px 0;
    `}
  >
    <Timeline timeline={timeline} />
  </div>
);

interface PostFooterProps {
  postId: number;
  postSlug: string;
  isLiked: boolean;
  likeCount: number;
  commentCount: number;
  onUpdate?: (post: ResCommunityPostPost | ResCommunityPostNews) => void;
}

const PostFooter: React.FC<PostFooterProps> = ({
  postId,
  postSlug,
  isLiked,
  likeCount,
  commentCount,
  onUpdate,
}) => {
  const theme = useTheme();
  const {
    state: { dataUser },
  } = useAuth();
  const router = useRouter();
  const color = theme.palette.common.black;
  const colorSecondary = theme.palette.grey[500];

  const handleOnCommentClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      if (!dataUser) {
        e.preventDefault();
        router.push({
          pathname: "/login",
          query: {
            redirect: router.asPath,
          },
        });
        return;
      }
      const commentField = document.getElementById("writeComment");
      if (commentField) {
        e.preventDefault();
        commentField.focus();
      }
    },
    [router, dataUser],
  );

  return (
    <div
      css={`
        display: flex;
        align-items: center;
        height: 60px;
        line-height: 20px;
        font-size: 14px;
        font-weight: 500;
      `}
    >
      <LikeButton
        css={`
          margin-right: 20px;
        `}
        postId={postId}
        isLiked={isLiked}
        likeCount={likeCount}
        onUpdate={onUpdate}
      />

      <Link href={`/community/posts/${postSlug}?writeComment=1`} passHref>
        <a
          onClick={handleOnCommentClick}
          css={`
            margin-right: 30px;
            display: flex;
            align-items: center;
            cursor: pointer;
            border-radius: 10px;
            padding: 10px 7px;
            &:hover {
              background-color: #f3f4f8;
            }
          `}
        >
          <IconComment
            fill={commentCount > 0 ? color : colorSecondary}
            css={`
              margin-right: 6px;
            `}
          />
          <span
            css={`
              color: ${commentCount > 0 ? color : colorSecondary};
            `}
          >
            {commentCount > 0 ? commentCount : "Comments"}
          </span>
        </a>
      </Link>
    </div>
  );
};

interface LinkCompProps {
  disabled: boolean;
  postSlug: string;
}

const LinkComp: React.FC<LinkCompProps> = ({
  disabled,
  postSlug,
  children,
}) => {
  if (disabled) return <>{children}</>;
  return (
    <Link href={`/community/posts/${postSlug}`} passHref>
      <a
        css={`
          color: inherit;
          :hover {
            color: inherit;
          }
        `}
      >
        {children}
      </a>
    </Link>
  );
};

interface LikeButtonProps {
  postId: number;
  isLiked: boolean;
  likeCount: number;
  onUpdate: (post: ResCommunityPostPost | any) => void;
  className?: string;
}

const LikeButton: React.FC<LikeButtonProps> = ({
  postId,
  isLiked,
  likeCount,
  onUpdate,
  className,
}) => {
  const { palette } = useTheme();
  const {
    state: { dataUser },
  } = useAuth();
  const { doUpdatePostLike } = useCommunity();
  const router = useRouter();
  const color = palette.common.black;
  const colorSecondary = palette.grey[500];
  const [localState, setLocalState] = useState<{
    isLiked: boolean;
    likeCount: number;
  }>({ isLiked, likeCount });

  const setIsLikedRemote = useMemo(
    () =>
      debounce(async (on: boolean) => {
        try {
          const data = await doUpdatePostLike(postId, on);
          onUpdate?.(data);
        } catch (e) {
          console.log(e);
          setLocalState((s) => ({
            isLiked: !on,
            likeCount: s.likeCount + (on ? -1 : 1),
          }));
        }
      }, 300),
    [postId, onUpdate, doUpdatePostLike],
  );

  const handleOnLikeClick = useCallback(() => {
    if (!dataUser) {
      router.push({
        pathname: "/login",
        query: {
          redirect: router.asPath,
        },
      });
      return;
    }
    setLocalState((s) => {
      setIsLikedRemote(!s.isLiked);
      return {
        isLiked: !s.isLiked,
        likeCount: s.likeCount + (s.isLiked ? -1 : 1),
      };
    });
  }, [dataUser, router, setIsLikedRemote]);

  // Synchronize source and local states.
  useEffect(() => {
    setLocalState({ isLiked, likeCount });
  }, [isLiked, likeCount]);

  return (
    <button
      css={`
        display: flex;
        align-items: center;
        border: none;
        background: none;
        line-height: 20px;
        font-size: 14px;
        font-weight: 500;
        color: ${localState.likeCount > 0 ? color : colorSecondary};
        cursor: pointer;
        border-radius: 10px;
        padding: 10px 7px;
        &:hover {
          background-color: #f3f4f8;
        }
      `}
      className={className}
      onClick={handleOnLikeClick}
    >
      <div
        css={`
          display: flex;
          margin-right: 6px;
        `}
      >
        {localState.isLiked ? (
          <IconHeartFilled />
        ) : (
          <IconHeart fill={localState.likeCount > 0 ? color : colorSecondary} />
        )}
      </div>
      {localState.likeCount > 0 ? localState.likeCount : "Like"}
    </button>
  );
};
